import { RefObject, useEffect } from "react"

import type { MapRef } from "react-map-gl/mapbox"

/**
 * Keeps a Mapbox map matched to its container.
 *
 * Mapbox only re-measures on `window.resize`, so a map whose container changes size for any
 * other reason keeps the size it was born with. On Monitoring that happens on nearly every
 * load: the map mounts, and then the late-routes alert band resolves and pushes the map area
 * down, leaving a canvas taller than the box that clips it — the bottom of the map is cropped
 * and the centre sits low. Same story for the filter bar and the stat band.
 *
 * A ResizeObserver on the container closes that gap: whatever moves, the map re-measures.
 *
 * @param mapRef the `ref` passed to react-map-gl's `<Map>`
 * @param containerRef the element the map is sized against
 */
export function useMapAutoResize(
  mapRef: RefObject<MapRef | null>,
  containerRef: RefObject<HTMLElement | null>
) {
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const observer = new ResizeObserver(() => {
      mapRef.current?.resize()
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [mapRef, containerRef])
}
